<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    class="min-h-screen bg-background font-body text-on-surface selection:bg-primary-fixed selection:text-on-primary-fixed"
  >
    <Header />

    <main v-if="article">
      <!-- Cinematic hero -->
      <section
        class="relative flex h-[80vh] min-h-[600px] items-center overflow-hidden"
      >
        <div class="absolute inset-0 z-0">
          <div
            ref="heroBgRef"
            class="h-full w-full bg-cover bg-center transition-transform duration-[10s] ease-out"
            :style="heroBackgroundStyle"
          ></div>
          <div
            class="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent"
          ></div>
        </div>

        <div
          class="article-hero-content relative z-10 w-full px-6 md:px-8 lg:px-12"
        >
          <div class="mx-auto w-full max-w-screen-xl">
            <div class="max-w-3xl">
              <div class="mb-6 flex flex-wrap items-center gap-4">
                <span
                  class="rounded-sm bg-secondary-container px-3 py-1 font-label text-label-md uppercase tracking-widest text-on-secondary-container"
                >
                  {{ article.category }}
                </span>
                <span
                  v-if="article.date"
                  class="font-label text-label-md uppercase tracking-wider text-primary-fixed"
                >
                  {{ article.date }}
                </span>
              </div>

              <h1
                class="article-hero-title mb-8 font-display text-display-lg-mobile leading-tight text-white md:text-display-lg"
              >
                {{ article.title }}
              </h1>

              <div v-if="article.author" class="flex items-center gap-6">
                <div class="flex items-center gap-3">
                  <div
                    class="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-2 border-primary-fixed bg-primary-container font-label text-sm font-semibold text-on-primary"
                  >
                    {{ authorInitials }}
                  </div>
                  <div>
                    <p class="font-label text-white">{{ article.author }}</p>
                    <p
                      v-if="article.readTime"
                      class="text-sm text-primary-fixed/80"
                    >
                      {{ article.readTime }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Article body + sidebar -->
      <section class="section-padding bg-surface">
        <div
          class="page-x mx-auto grid max-w-screen-xl grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16"
        >
          <article class="lg:col-span-8">
            <div class="mb-12 flex items-center justify-between gap-4">
              <router-link
                to="/articles"
                class="group flex items-center gap-2 font-label text-primary transition-transform hover:-translate-x-1"
              >
                <span class="material-symbols-outlined text-sm">arrow_back</span>
                Back to All Insights
              </router-link>

              <div class="flex items-center gap-4 text-on-surface-variant">
                <button
                  type="button"
                  class="transition-colors hover:text-primary"
                  aria-label="Share article"
                  @click="shareToLinkedIn"
                >
                  <span class="material-symbols-outlined">share</span>
                </button>
                <button
                  type="button"
                  class="transition-colors hover:text-primary"
                  aria-label="Copy article link"
                  @click="copyLink"
                >
                  <span class="material-symbols-outlined">{{
                    linkCopied ? "bookmark_added" : "bookmark"
                  }}</span>
                </button>
              </div>
            </div>

            <div class="article-content max-w-none space-y-8">
              <div
                v-html="article.content"
                class="wysiwyg-content article-prose"
              ></div>
            </div>

            <div
              class="mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-outline-variant/30 pt-8"
            >
              <div v-if="articleTags.length" class="flex flex-wrap items-center gap-4">
                <span class="font-label text-on-surface-variant">Tags:</span>
                <span
                  v-for="tag in articleTags"
                  :key="tag"
                  class="rounded-full bg-surface-container-high px-3 py-1 text-xs font-medium uppercase text-on-surface-variant transition-colors hover:bg-primary hover:text-white"
                >
                  {{ tag }}
                </span>
              </div>

              <div class="flex items-center gap-3">
                <span class="font-label text-on-surface-variant">Share:</span>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="flex h-8 w-8 items-center justify-center rounded-full border border-outline-variant transition-all hover:bg-primary hover:text-white"
                    aria-label="Copy link"
                    @click="copyLink"
                  >
                    <span class="material-symbols-outlined text-sm">link</span>
                  </button>
                  <a
                    :href="mailtoShareUrl"
                    class="flex h-8 w-8 items-center justify-center rounded-full border border-outline-variant transition-all hover:bg-primary hover:text-white"
                    aria-label="Share by email"
                  >
                    <span class="material-symbols-outlined text-sm">mail</span>
                  </a>
                </div>
              </div>
            </div>
          </article>

          <aside class="space-y-16 lg:col-span-4">
            <div
              v-if="sidebarArticles.length"
              class="sticky top-32 rounded-xl bg-surface-container-low p-8"
            >
              <h4 class="mb-6 font-headline text-headline-md text-primary">
                Strategic Insights
              </h4>
              <div class="space-y-6">
                <template
                  v-for="(item, index) in sidebarArticles"
                  :key="item.id"
                >
                  <router-link
                    :to="`/article/${item.id}`"
                    class="group block"
                  >
                    <span
                      class="mb-1 block font-label text-xs uppercase tracking-widest text-secondary"
                    >
                      {{ item.category }}
                    </span>
                    <p
                      class="font-body text-body-md font-semibold text-on-surface transition-colors group-hover:text-primary"
                    >
                      {{ item.title }}
                    </p>
                    <span
                      v-if="item.readTime"
                      class="mt-2 block text-xs uppercase text-on-surface-variant"
                    >
                      {{ item.readTime }}
                    </span>
                  </router-link>
                  <div
                    v-if="index < sidebarArticles.length - 1"
                    class="h-px bg-outline-variant/30"
                  ></div>
                </template>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <!-- Further perspectives -->
      <section
        v-if="similarArticles.length"
        class="section-padding bg-surface-container-lowest"
      >
        <div class="page-x mx-auto max-w-screen-xl">
          <h2 class="mb-8 font-headline text-headline-lg text-primary md:mb-12">
            Related <span class="text-brand-yellow">Articles</span>
          </h2>
          <div class="grid grid-cols-1 gap-8 md:grid-cols-3">
            <article
              v-for="item in similarArticles"
              :key="item.id"
              class="group cursor-pointer"
              @click="router.push(`/article/${item.id}`)"
            >
              <div class="mb-4 aspect-[16/10] overflow-hidden rounded-xl">
                <img
                  :src="item.image"
                  :alt="item.title"
                  class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <span
                class="font-label text-xs uppercase tracking-widest text-secondary"
              >
                {{ item.category }}
              </span>
              <h3
                class="mt-2 font-headline text-headline-md leading-snug text-on-surface transition-colors group-hover:text-primary"
              >
                {{ item.title }}
              </h3>
            </article>
          </div>
        </div>
      </section>
    </main>

    <div
      v-if="isLoading"
      class="flex min-h-screen items-center justify-center bg-background pb-24 pt-28"
    >
      <div class="text-center">
        <div
          class="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-secondary border-t-transparent"
        ></div>
        <p class="font-body text-body-md font-medium text-on-surface-variant">
          Loading article…
        </p>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script setup>
/* eslint-disable vue/multi-word-component-names */
import { ref, onMounted, watch, onBeforeUnmount, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import Header from "./Header.vue";
import Footer from "./Footer.vue";
import { getArticleById, getSimilarArticles } from "../utils/articles.js";
import { trackArticleView } from "../utils/analytics.js";
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  DEFAULT_DESCRIPTION,
  setRobotsMeta,
} from "../utils/seo.js";
import "../assets/quill-content.css";

const route = useRoute();
const router = useRouter();
const article = ref(null);
const similarArticles = ref([]);
const linkCopied = ref(false);
const heroBgRef = ref(null);
const baseUrl = SITE_URL;
const isLoading = ref(true);

const articleUrl = computed(() => {
  if (typeof window !== "undefined") {
    return window.location.href;
  }
  return "";
});

const heroBackgroundStyle = computed(() => {
  if (article.value?.image) {
    return { backgroundImage: `url(${article.value.image})` };
  }
  return {
    backgroundImage:
      "linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-container) 100%)",
  };
});

const authorInitials = computed(() => {
  const name = article.value?.author || "";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
});

const articleTags = computed(() => {
  if (!article.value) return [];
  if (Array.isArray(article.value.categories) && article.value.categories.length) {
    return article.value.categories;
  }
  return article.value.category ? [article.value.category] : [];
});

const sidebarArticles = computed(() => similarArticles.value.slice(0, 3));

const mailtoShareUrl = computed(() => {
  if (!article.value) return "#";
  const subject = encodeURIComponent(article.value.title);
  const body = encodeURIComponent(`${article.value.title}\n\n${articleUrl.value}`);
  return `mailto:?subject=${subject}&body=${body}`;
});

const shareToLinkedIn = () => {
  const url = encodeURIComponent(articleUrl.value);
  window.open(
    `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    "_blank",
    "width=550,height=420",
  );
};

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(articleUrl.value);
    linkCopied.value = true;
    setTimeout(() => {
      linkCopied.value = false;
    }, 2000);
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = articleUrl.value;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      linkCopied.value = true;
      setTimeout(() => {
        linkCopied.value = false;
      }, 2000);
    } catch (err) {
      console.error("Failed to copy link", err);
    }
    document.body.removeChild(textArea);
  }
};

const ensureAbsoluteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  if (url.startsWith("/")) {
    return `${baseUrl}${url}`;
  }
  return `${baseUrl}/${url}`;
};

const updateMetaTags = (articleData) => {
  if (!articleData || typeof document === "undefined") return;

  const articlePath = `${baseUrl}/article/${articleData.id}`;
  const siteName = SITE_NAME;
  const imageUrl = ensureAbsoluteUrl(articleData.image);

  const metaTags = {
    title: articleData.title,
    description: articleData.excerpt,
    "og:title": articleData.title,
    "og:description": articleData.excerpt,
    "og:image": imageUrl,
    "og:image:width": "1200",
    "og:image:height": "630",
    "og:image:type": "image/jpeg",
    "og:image:alt": articleData.title,
    "og:url": articlePath,
    "og:type": "article",
    "og:site_name": siteName,
    "og:locale": "en_US",
    "twitter:card": "summary_large_image",
    "twitter:title": articleData.title,
    "twitter:description": articleData.excerpt,
    "twitter:image": imageUrl,
    "twitter:image:alt": articleData.title,
    "twitter:site": "@brandintegrated",
    "article:published_time": new Date(articleData.date).toISOString(),
    "article:author": articleData.author || siteName,
    "article:section": articleData.categories?.[0] || articleData.category,
    "article:tag": articleData.categories?.[0] || articleData.category,
    author: articleData.author || siteName,
  };

  setRobotsMeta("index, follow");
  document.title = `${articleData.title} | ${siteName}`;

  Object.keys(metaTags).forEach((key) => {
    let meta =
      document.querySelector(`meta[property="${key}"]`) ||
      document.querySelector(`meta[name="${key}"]`);

    if (!meta) {
      meta = document.createElement("meta");
      if (key.startsWith("og:") || key.startsWith("article:")) {
        meta.setAttribute("property", key);
      } else {
        meta.setAttribute("name", key);
      }
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", metaTags[key]);
  });

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", articlePath);
};

const addJsonLd = (articleData) => {
  if (!articleData || typeof document === "undefined") return;

  const existingScript = document.querySelector(
    'script[type="application/ld+json"]',
  );
  if (existingScript) {
    existingScript.remove();
  }

  const articlePath = `${baseUrl}/article/${articleData.id}`;
  const publishedDate = new Date(articleData.date).toISOString();
  const imageUrl = ensureAbsoluteUrl(articleData.image);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: articleData.title,
    description: articleData.excerpt,
    image: imageUrl,
    datePublished: publishedDate,
    dateModified: publishedDate,
    author: {
      "@type": articleData.author ? "Person" : "Organization",
      name: articleData.author || "Brand Integrated",
      url: articleData.author ? `${baseUrl}/team` : baseUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Brand Integrated",
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_OG_IMAGE,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": articlePath,
    },
    articleSection: articleData.categories?.[0] || articleData.category,
    keywords: (articleData.categories || []).join(", "),
  };

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.text = JSON.stringify(jsonLd);
  document.head.appendChild(script);
};

const resetMetaTags = () => {
  if (typeof document === "undefined") return;
  document.title = SITE_NAME;
  setRobotsMeta("index, follow");

  const defaultMeta = {
    description: DEFAULT_DESCRIPTION,
    "og:title": "Brand Integrated - Future Defined",
    "og:description": DEFAULT_DESCRIPTION,
    "og:image": DEFAULT_OG_IMAGE,
    "og:url": baseUrl,
    "og:type": "website",
    "twitter:title": "Brand Integrated - Future Defined",
    "twitter:description": DEFAULT_DESCRIPTION,
    "twitter:image": DEFAULT_OG_IMAGE,
  };

  Object.keys(defaultMeta).forEach((key) => {
    const meta =
      document.querySelector(`meta[property="${key}"]`) ||
      document.querySelector(`meta[name="${key}"]`);
    if (meta) {
      meta.setAttribute("content", defaultMeta[key]);
    }
  });

  document
    .querySelectorAll('meta[property^="article:"]')
    .forEach((meta) => meta.remove());

  const jsonLdScript = document.querySelector(
    'script[type="application/ld+json"]',
  );
  if (jsonLdScript) {
    jsonLdScript.remove();
  }
};

const onHeroScroll = () => {
  const hero = heroBgRef.value;
  if (!hero) return;
  const scrolled = window.pageYOffset;
  hero.style.transform = `translateY(${scrolled * 0.4}px) scale(${1 + scrolled * 0.0005})`;
};

const loadArticle = async (articleId) => {
  if (!articleId) return;

  isLoading.value = true;
  article.value = null;
  similarArticles.value = [];

  try {
    const fetchedArticle = await getArticleById(articleId);

    if (!fetchedArticle) {
      router.push("/");
      return;
    }

    article.value = fetchedArticle;
    trackArticleView(article.value.id);
    updateMetaTags(article.value);
    addJsonLd(article.value);

    const similar = await getSimilarArticles(
      article.value.categories,
      article.value.id,
      3,
    );
    similarArticles.value = similar || [];
  } catch (error) {
    console.error("Error loading article:", error);
    router.push("/");
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  window.addEventListener("scroll", onHeroScroll, { passive: true });
  await loadArticle(route.params.id);
});

watch(
  () => route.params.id,
  async (newId) => {
    if (newId) {
      await loadArticle(newId);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  },
);

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onHeroScroll);
  resetMetaTags();
});
</script>

<style scoped>
.article-hero-content {
  padding-top: 6.5rem;
}

@media (min-width: 768px) {
  .article-hero-content {
    padding-top: 7.5rem;
  }
}

@media (min-width: 1024px) {
  .article-hero-content {
    padding-top: 8.5rem;
  }
}

.article-hero-title {
  text-shadow: 0 0 20px rgba(161, 212, 148, 0.3);
  text-wrap: balance;
}

.article-content {
  font-family: var(--font-body);
  color: var(--color-on-surface-variant);
}

.article-content :deep(.wysiwyg-content > p:first-of-type) {
  float: none;
  margin-bottom: 2rem;
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--color-on-surface-variant);
}

.article-content :deep(.wysiwyg-content > p:first-of-type::first-letter) {
  float: left;
  margin-right: 0.75rem;
  font-size: 4.5rem;
  line-height: 0.85;
  font-weight: 700;
  color: var(--color-primary);
}

.article-content :deep(p) {
  margin-bottom: 1.5rem;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-on-surface-variant);
}

.article-content :deep(h1),
.article-content :deep(h2),
.article-content :deep(h3),
.article-content :deep(h4),
.article-content :deep(h5),
.article-content :deep(h6) {
  font-family: var(--font-headline);
  font-weight: 600;
  color: var(--color-primary);
  margin-top: 2rem;
  margin-bottom: 1rem;
  line-height: 1.3;
}

.article-content :deep(h2) {
  font-size: 2rem;
}

.article-content :deep(h3) {
  font-size: 1.5rem;
}

.article-content :deep(a) {
  color: var(--color-secondary);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.article-content :deep(a:hover) {
  color: var(--color-primary);
}

.article-content :deep(blockquote) {
  margin: 2rem 0;
  padding: 2rem;
  border-left: 4px solid var(--color-secondary);
  border-radius: 0 0.5rem 0.5rem 0;
  background: var(--color-surface-container-lowest);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  font-style: italic;
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--color-primary);
}

.article-content :deep(figure) {
  margin: 3rem 0;
  overflow: hidden;
  border-radius: 0.75rem;
  background: var(--color-surface-container-low);
  padding: 0.5rem;
}

.article-content :deep(img) {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 2rem 0;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
}

.article-content :deep(figcaption) {
  margin-top: 1rem;
  text-align: center;
  font-family: var(--font-label);
  font-size: 0.875rem;
  color: var(--color-on-surface-variant);
  opacity: 0.7;
}

.article-content :deep(ul),
.article-content :deep(ol) {
  margin-bottom: 1.5rem;
  padding-left: 1.25rem;
}

.article-content :deep(li) {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.article-content :deep(strong) {
  font-weight: 600;
  color: var(--color-primary);
}

.article-content :deep(pre) {
  margin: 1.5rem 0;
  padding: 1rem;
  overflow-x: auto;
  border-radius: 0.5rem;
  background-color: var(--color-surface-container-low);
}

.article-content :deep(code) {
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  background-color: var(--color-surface-container);
}

.article-content :deep(table) {
  width: 100%;
  margin: 1.5rem 0;
  border-collapse: collapse;
}

.article-content :deep(th),
.article-content :deep(td) {
  padding: 0.75rem;
  border: 1px solid var(--color-outline-variant);
  text-align: left;
}

.article-content :deep(th) {
  font-weight: 600;
  background-color: var(--color-surface-container-low);
  color: var(--color-primary);
}

.article-content :deep(hr) {
  margin: 2rem 0;
  border: none;
  border-top: 1px solid var(--color-outline-variant);
}
</style>
